import { Component, Input, OnDestroy, ViewChild, OnChanges, ComponentFactoryResolver } from '@angular/core'
import { FlowplayerService } from './flowplayer.service'
import { FlowplayerClipFactory, VideoTypeDefinition } from './flowplayer-clip.factory'
import { IFlowplayerVideoPlayer } from './flowplayer-video-player.interface'
import {
  FlowplayerModelRendererComponent,
  FlowplayerRendererDirective,
  FlowplayerNormalRendererComponent
} from './renderers'

const RENDERER_MAP = {
  normal: FlowplayerNormalRendererComponent
}

@Component({
  selector: 'mp-flowplayer',
  templateUrl: './flowplayer.html',
  styleUrls: ['./flowplayer.scss']
})
export class FlowplayerComponent implements OnDestroy, OnChanges {
  public static readonly DEFAULT_HEIGHT: string = '700px'

  @Input()
  public rendererType: string

  @Input()
  public height: string = FlowplayerComponent.DEFAULT_HEIGHT

  @Input()
  public uri: string

  private _elementId: string
  private _renderer

  @ViewChild(FlowplayerRendererDirective)
  private _rendererHost: FlowplayerRendererDirective

  private get _playerContainerElement(): HTMLElement {
    return this._renderer.playerContainerRef.nativeElement
  }

  private _player: IFlowplayerVideoPlayer

  constructor(
    private _flowPlayerService: FlowplayerService,
    private _flowplayerClipFactory: FlowplayerClipFactory,
    private _componentFactoryResolver: ComponentFactoryResolver,
  ) {}

  public ngOnDestroy() {
    this.shutdownPlayer()
  }

  public ngOnChanges() {
    this._loadRenderer()
    if (!!this.uri) {
      this.render(this.uri)
    }
  }

  public render(url: string, type?: VideoTypeDefinition) {
    if (!this._renderer) {
      this._loadRenderer()
    }
    this._initializePlayerElement()
    this._loadVideoInPlayer(url, type)
    this._renderer.render()
  }

  public shutdownPlayer() {
    if (this._player) {
      this._player.stop()
      this._player.shutdown()
      this._player = undefined
    }
  }

  private _initializePlayerElement() {
    this.shutdownPlayer()
    this._createPlayerElement()
  }

  private _createPlayerElement() {
    this._elementId = this._generateRandomElementId()
    this._playerContainerElement.innerHTML = `
      <div id="${this._elementId}" class="flowplayer" style="height: ${this.height};">
      </div>
    `
  }

  private _generateRandomElementId() {
    const randomId = Math.random().toString(36).substring(7)
    return `flowplayer_instance_${randomId}`
  }

  private _loadVideoInPlayer(url: string, type: VideoTypeDefinition) {
    const clip = this._flowplayerClipFactory.createClip(url, type)
    this._player = this._flowPlayerService.createOrFindBySelector(
      `#${this._elementId}`, {clip:  clip}
    )
  }

  private _loadRenderer() {
    const rendererComponent = this._getRendererComponent()
    const componentFactory = this._componentFactoryResolver.resolveComponentFactory(rendererComponent)
    const viewContainerRef = this._rendererHost.viewContainerRef

    viewContainerRef.clear()

    this._renderer = viewContainerRef.createComponent(componentFactory).instance
    this._renderer.shutdown.subscribe(() => this.shutdownPlayer())
  }

  private _getRendererComponent() {
    return RENDERER_MAP[this.rendererType] || FlowplayerModelRendererComponent
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/angular/components/flowplayer/flowplayer.component.ts